/**
 * CurrentTemplateContext
 * Stores the active template name for PostHog pageview/route_change when on "/".
 * Ensures PostHog + session replay can be filtered by template for all home templates
 * (including Mux DualCTA / InvitacionExclusivaMux — the international default).
 */

import { createContext, useCallback, useContext, useState, type ReactNode } from "react";

/** All templates that may own the home `/` or a full-page landing with PostHog filtering. */
export type TemplateName =
  | "BaseTemplate"
  | "BaseTemplateInternational"
  | "BioLinkTemplate"
  | "ArgentinaBioTemplate"
  | "MaintenanceTemplate"
  | "DualCTAVideoLanding"
  | "InvitacionExclusivaMux"
  | "BioTemplatePremiumSimple"
  | "BioTemplatePremium"
  | "InvitacionMiembroTemplate"
  | "BaseTemplateBlack"
  | null;

interface CurrentTemplateContextValue {
  templateName: TemplateName;
  setTemplateName: (name: string | null) => void;
}

const CurrentTemplateContext = createContext<CurrentTemplateContextValue | null>(null);

const VALID_TEMPLATES: readonly string[] = [
  "BaseTemplate",
  "BaseTemplateInternational",
  "BioLinkTemplate",
  "ArgentinaBioTemplate",
  "MaintenanceTemplate",
  "DualCTAVideoLanding",
  "InvitacionExclusivaMux",
  "BioTemplatePremiumSimple",
  "BioTemplatePremium",
  "InvitacionMiembroTemplate",
  "BaseTemplateBlack",
];

export function CurrentTemplateProvider({ children }: { children: ReactNode }) {
  const [templateName, setTemplateNameState] = useState<TemplateName>(null);
  const setTemplateName = useCallback((name: string | null) => {
    setTemplateNameState(
      name && VALID_TEMPLATES.includes(name) ? (name as TemplateName) : null,
    );
  }, []);

  return (
    <CurrentTemplateContext.Provider value={{ templateName, setTemplateName }}>
      {children}
    </CurrentTemplateContext.Provider>
  );
}

export function useCurrentTemplate(): CurrentTemplateContextValue {
  const ctx = useContext(CurrentTemplateContext);
  if (!ctx) {
    return {
      templateName: null,
      setTemplateName: (_: string | null) => {},
    };
  }
  return ctx;
}
